import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/api';
import EmptyState from '../shared/EmptyState';
import './AIProductivitySummary.css';

interface AiData {
  ai_generated_count: number;
  adoption_percentage: number;
  estimated_hours_saved: number;
  estimated_working_days_saved: number;
  constants_used: Record<string, number>;
}

interface Props { days: number; showFooterLink?: boolean; }

export const AIProductivitySummary: React.FC<Props> = ({ days, showFooterLink = true }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AiData | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/api/v1/dashboard/ai-productivity?days=${days}`)
      .then(resp => { if (!cancelled) setData(resp.data as AiData); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="aip-card">
      <div className="aip-header"><h3>AI Productivity</h3></div>
      {loading ? (
        <div className="aip-skeleton" />
      ) : !data || data.ai_generated_count === 0 ? (
        <EmptyState
          message="No AI-generated content yet this period."
          actionLabel="Generate scenarios with AI"
          actionLink="/test-scenarios"
        />
      ) : (
        <>
          <div className="aip-flow">
            <div className="aip-flow-step" title="Scenarios + test cases created via AI generation in the selected period.">
              <span className="aip-flow-value">{data.ai_generated_count.toLocaleString()}</span>
              <span className="aip-flow-label">AI-generated items</span>
            </div>
            <span className="aip-flow-arrow">→</span>
            <div className="aip-flow-step" title="Estimated hours saved vs. writing these manually — see the formula below. Not a measured value.">
              <span className="aip-flow-value">{data.estimated_hours_saved}h</span>
              <span className="aip-flow-label">Est. time saved</span>
            </div>
            <span className="aip-flow-arrow">→</span>
            <div className="aip-flow-step" title="Estimated time saved converted into 8-hour working days.">
              <span className="aip-flow-value">≈{data.estimated_working_days_saved}</span>
              <span className="aip-flow-label">Working days</span>
            </div>
            <span className="aip-flow-arrow">→</span>
            <div className="aip-flow-step" title="% of all scenarios + test cases created this period that were AI-generated.">
              <span className="aip-flow-value">{data.adoption_percentage}%</span>
              <span className="aip-flow-label">Adoption</span>
            </div>
          </div>
          <p className="aip-formula">
            <strong>Estimated</strong>, not measured: (AI-generated count × avg. manual creation time) − (count × avg. AI-review time),
            using admin-configured constants ({data.constants_used.avg_manual_scenario_min}m/scenario, {data.constants_used.avg_manual_testcase_min}m/test case, {data.constants_used.avg_ai_review_min}m review).
          </p>
        </>
      )}
      {showFooterLink && (
        <div className="aip-footer">
          <Link to="/reports?tab=ai">View AI Productivity Report →</Link>
        </div>
      )}
    </div>
  );
};

export default AIProductivitySummary;
